import { escapeHtml } from './helpers';
import { resolveEmailImageUrl } from './emailAssetUrl';
import {
  filterFooterLinks,
  footerInlineLinkHtml,
  footerSocialButtonHtml,
  linkAlignForEmail,
  normalizeFooterTemplate,
} from './footerBlockUtils';
import { footerSocialIconEmailHtml } from './footerSocialIcons';

export type FooterBlockContent = Record<string, any>;
export type FooterBlockStyles = Record<string, any>;

type EmailAlign = 'left' | 'center' | 'right';

function wrapFooterLinkRow(innerHtml: string, align: EmailAlign, extra = ''): string {
  if (!innerHtml.trim()) return '';
  return `<table role="presentation" class="footer-link-row" cellspacing="0" cellpadding="0" border="0" width="100%" style="width:100%;${extra}">
    <tr><td align="${align}" style="text-align:${align};line-height:1.6;">${innerHtml}</td></tr>
  </table>`;
}

function wrapFooterSocialRow(buttons: string[], align: EmailAlign, extra = ''): string {
  if (!buttons.length) return '';
  const cells = buttons
    .map(
      (btn) =>
        `<td align="center" valign="middle" style="padding:0 5px;line-height:0;font-size:0;text-align:center;vertical-align:middle;width:auto !important;max-width:none !important;">${btn}</td>`
    )
    .join('');
  return `<table role="presentation" class="footer-social-row" cellspacing="0" cellpadding="0" border="0" align="${align}" style="width:auto !important;max-width:none !important;margin:0 auto;${extra}">
    <tr>${cells}</tr>
  </table>`;
}

function buildSocialButtons(
  content: FooterBlockContent,
  boxSize: number,
  iconSize: number,
  bgColor: string,
  iconColor: string,
  radius: string | number
): string[] {
  if (!content.socialPlatforms?.length) return [];
  return content.socialPlatforms.map((platform: any) => {
    const isCustom = platform.icon === 'custom' && platform.customIconUrl;
    const iconHtml = isCustom
      ? `<img src="${escapeHtml(resolveEmailImageUrl(platform.customIconUrl))}" alt="${escapeHtml(platform.name || 'Social')}" width="${iconSize}" height="${iconSize}" class="footer-social-icon-img" style="width:${iconSize}px !important;height:${iconSize}px !important;max-width:${iconSize}px !important;min-width:${iconSize}px !important;display:block;margin:0 auto;object-fit:contain;" />`
      : footerSocialIconEmailHtml(
          platform.icon || 'facebook',
          iconColor,
          iconSize,
          platform.name || ''
        );
    return footerSocialButtonHtml(platform.url || '#', iconHtml, boxSize, bgColor, radius, platform.name || '');
  });
}

export const renderFooterBlockHtml = (
  content: FooterBlockContent,
  styles: FooterBlockStyles,
  fontFamily: string,
  isMobile = false
): string => {
  const template = normalizeFooterTemplate(content.template);
  const ta = String(styles.textAlign || '');
  const align = linkAlignForEmail(ta);
  const padding = styles.padding || '40px 24px';
  const bg = styles.backgroundColor || '#F8FAFC';
  const fontSize = styles.fontSize || '14px';
  const color = styles.color || '#64748B';
  const outerStyle = `padding:${padding};background-color:${bg};font-size:${fontSize};color:${color};font-family:${fontFamily};`;

  if (template === 'complete') {
    const completeAlign = ta === 'justify' ? 'center' : ta || (isMobile ? 'center' : 'left');
    const socialAlign: EmailAlign = isMobile ? 'center' : completeAlign === 'right' ? 'right' : completeAlign === 'left' ? 'left' : 'center';
    const socialButtons = buildSocialButtons(
      content,
      40,
      20,
      content.socialBgColor || '#111827',
      content.socialIconColor || '#FFFFFF',
      '10px'
    );
    const socialRow = socialButtons.length ? wrapFooterSocialRow(socialButtons, socialAlign) : '';
    const bottomRow = socialRow
      ? `<table class="footer-complete-bottom" role="presentation" width="100%" cellspacing="0" cellpadding="0" border="0" align="${socialAlign}" style="width:100%;margin-top:24px;padding-top:20px;border-top:1px solid #E5E7EB;">
          <tr><td align="${socialAlign}" style="text-align:${socialAlign};">${socialRow}</td></tr>
        </table>`
      : '';
    const apps =
      content.appStoreUrl || content.googlePlayUrl
        ? `${content.appHeading ? `<h4 style="font-size:16px;font-weight:600;color:#111827;margin:0 0 12px;">${escapeHtml(content.appHeading)}</h4>` : ''}
        <table role="presentation" cellspacing="0" cellpadding="0" border="0"><tr>
          ${content.appStoreUrl ? `<td style="padding-right:8px;"><a href="${content.appStoreUrl}" style="display:inline-block;background:#000;color:#fff;padding:10px 16px;border-radius:6px;font-size:13px;font-weight:600;text-decoration:none;">Download on the App Store</a></td>` : ''}
          ${content.googlePlayUrl ? `<td><a href="${content.googlePlayUrl}" style="display:inline-block;background:#000;color:#fff;padding:10px 16px;border-radius:6px;font-size:13px;font-weight:600;text-decoration:none;">GET IT ON Google Play</a></td>` : ''}
        </tr></table>`
        : '';
    return `<table role="presentation" cellspacing="0" cellpadding="0" border="0" width="100%" style="width:100%;border-collapse:collapse;"><tr><td style="${outerStyle}text-align:${completeAlign};">
      ${content.companyName ? `<h3 style="font-size:20px;font-weight:700;color:#111827;margin:0 0 8px;">${escapeHtml(content.companyName)}</h3>` : ''}
      ${content.location ? `<p style="margin:0 0 16px;line-height:1.6;white-space:pre-line;">${escapeHtml(content.location)}</p>` : ''}
      ${content.disclaimer ? `<p style="margin:0 0 24px;font-size:13px;line-height:1.5;color:#6B7280;">${escapeHtml(content.disclaimer)}</p>` : ''}
      ${apps}
      ${bottomRow}
    </td></tr></table>`;
  }

  if (template === 'minimal') {
    const footerLinks = filterFooterLinks(content.links)
      ?.map((link: any) =>
        footerInlineLinkHtml(link.text, link.url || '#', `color:${color};font-size:11px;font-weight:600;text-transform:uppercase;`)
      )
      .join('');
    return `<table role="presentation" cellspacing="0" cellpadding="0" border="0" width="100%" style="width:100%;border-collapse:collapse;"><tr><td style="${outerStyle}text-align:${align};border-top:1px solid #F1F5F9;">
      ${content.text ? `<p style="margin:0 0 16px;line-height:1.6;">${escapeHtml(content.text)}</p>` : ''}
      ${footerLinks ? wrapFooterLinkRow(footerLinks, align) : ''}
    </td></tr></table>`;
  }

  if (template === 'gradient') {
    const gradientBg = String(bg).includes('gradient') ? bg : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)';
    const footerLinks = filterFooterLinks(content.links)
      ?.map((link: any) =>
        footerInlineLinkHtml(link.text, link.url || '#', 'color:#fff;font-size:13px;opacity:0.9;')
      )
      .join('');
    const socialButtons = buildSocialButtons(content, 40, 20, 'rgba(255,255,255,0.2)', '#FFFFFF', '50%');
    return `<div style="padding:${padding};background:${gradientBg};text-align:${align};font-size:${fontSize};color:#fff;font-family:${fontFamily};">
      ${content.text ? `<p style="margin:0 0 20px;line-height:1.6;opacity:0.95;">${escapeHtml(content.text)}</p>` : ''}
      ${footerLinks ? wrapFooterLinkRow(footerLinks, align, 'margin-bottom:20px;') : ''}
      ${socialButtons.length ? wrapFooterSocialRow(socialButtons, align) : ''}
    </td></tr></table>`;
  }

  if (template === 'social') {
    const footerLinks = filterFooterLinks(content.links)
      ?.map((link: any) =>
        footerInlineLinkHtml(link.text, link.url || '#', `color:${color};font-size:13px;`)
      )
      .join('');
    const socialButtons = buildSocialButtons(content, 44, 22, '#FFFFFF', '#0F172A', '50%');
    return `<table role="presentation" cellspacing="0" cellpadding="0" border="0" width="100%" style="width:100%;border-collapse:collapse;"><tr><td style="padding:${padding};background-color:${bg || '#0F172A'};text-align:center;font-size:${fontSize};color:${color};font-family:${fontFamily};">
      ${content.companyName ? `<h3 style="margin:0 0 12px;font-size:22px;font-weight:700;color:#fff;">${escapeHtml(content.companyName)}</h3>` : ''}
      ${content.text ? `<p style="margin:0 0 28px;line-height:1.6;opacity:0.85;">${escapeHtml(content.text)}</p>` : ''}
      ${socialButtons.length ? wrapFooterSocialRow(socialButtons, 'center', 'margin-bottom:28px;') : ''}
      ${footerLinks ? wrapFooterLinkRow(footerLinks, 'center', 'padding-top:24px;border-top:1px solid rgba(255,255,255,0.12);') : ''}
    </td></tr></table>`;
  }

  // standard
  const footerLinks = filterFooterLinks(content.links)
    ?.map((link: any) =>
      footerInlineLinkHtml(link.text, link.url || '#', `color:${color};font-size:13px;`)
    )
    .join('');
  return `<table role="presentation" cellspacing="0" cellpadding="0" border="0" width="100%" style="width:100%;border-collapse:collapse;"><tr><td style="${outerStyle}text-align:${align};">
    ${content.text ? `<p style="margin:0 0 16px;line-height:1.6;">${escapeHtml(content.text)}</p>` : ''}
    ${footerLinks ? wrapFooterLinkRow(footerLinks, align) : ''}
  </td></tr></table>`;
};
